import { sidePaneMolecule } from '@web-app/atoms/in-session/sidePaneMolecule';
import { useInSessionClinicalGoals } from '@web-app/utils/feature-flags/in-session-clinical-goals';
import { useIsPrescriberFlow } from '@web-app/utils/feature-flags/prescriber-flow';
import { useMolecule } from 'bunshi/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import { SidePaneTabId } from '../sidePaneTabIds';
import { SidePaneTabConfig } from './types';

export interface UseSidePaneTabsReturn {
  visibleTabs: SidePaneTabConfig[];
  activeTabId: SidePaneTabId;
  activeIndex: number;
  handleTabChange: (index: number) => void;
  setActiveTab: (tabId: SidePaneTabId) => void;
  isTabActive: (tabId: SidePaneTabId) => boolean;
}

/**
 * Hook to manage side pane tab configuration and state.
 * Handles feature flag checks and filters visible tabs.
 */
export function useSidePaneTabs(): UseSidePaneTabsReturn {
  const { activeSidePaneTabAtom } = useMolecule(sidePaneMolecule);
  const [activeTabId, setActiveTab] = useAtom(activeSidePaneTabAtom);
  const { show_tab: showClinicalGoalsTab } = useInSessionClinicalGoals();
  const isPrescriberFlow = useIsPrescriberFlow();

  const visibleTabs: SidePaneTabConfig[] = useMemo(
    () =>
      [
        {
          id: SidePaneTabId.PREP,
          label: 'Prep',
          testId: 'tab-prep',
          errorBoundaryName: 'Prep',
          noPadding: true,
          isVisible: true,
          needsActiveState: true,
        },
        {
          id: SidePaneTabId.NOTES,
          label: 'Notes',
          testId: 'tab-notes',
          errorBoundaryName: 'Notes',
          isVisible: true,
        },
        {
          id: SidePaneTabId.GOALS,
          label: 'Clinical Goals',
          testId: 'tab-goals',
          errorBoundaryName: 'Goals',
          isVisible: showClinicalGoalsTab,
        },
        {
          id: SidePaneTabId.PATIENT,
          label: 'Patient',
          testId: 'tab-patient',
          errorBoundaryName: 'Patient',
          noPadding: true,
          isVisible: true,
          needsActiveState: true,
        },
        {
          id: SidePaneTabId.CHART_HISTORY,
          label: 'Chart History',
          testId: 'tab-chart-history',
          errorBoundaryName: 'Chart History',
          isVisible: true,
        },
        {
          id: SidePaneTabId.JOURNAL,
          label: 'Journal',
          testId: 'tab-journal',
          errorBoundaryName: 'Journal',
          noPadding: true,
          isVisible: !isPrescriberFlow,
          needsActiveState: true,
        },
      ].filter((tab) => tab.isVisible) as SidePaneTabConfig[],
    [showClinicalGoalsTab, isPrescriberFlow],
  );

  // If the atom holds a tab ID that's no longer visible, reset to the first visible tab
  // so that activeIndex and isTabActive stay in sync.
  useEffect(() => {
    const isActiveTabVisible = visibleTabs.some(
      (tab) => tab.id === activeTabId,
    );
    if (!isActiveTabVisible && visibleTabs[0]) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTabId, setActiveTab]);

  const activeIndex = useMemo(
    () =>
      Math.max(
        0,
        visibleTabs.findIndex((tab) => tab.id === activeTabId),
      ),
    [visibleTabs, activeTabId],
  );

  const handleTabChange = useCallback(
    (index: number) => {
      const tab = visibleTabs[index];
      if (tab) {
        setActiveTab(tab.id);
      }
    },
    [visibleTabs, setActiveTab],
  );

  const isTabActive = useCallback(
    (tabId: SidePaneTabId) => activeTabId === tabId,
    [activeTabId],
  );

  return {
    visibleTabs,
    activeTabId,
    activeIndex,
    handleTabChange,
    setActiveTab,
    isTabActive,
  };
}
